import { State } from '../State';
import { PetState } from '../pet';
import {
  ChatbotGroup,
  ChatMessageType,
  RichMenuName,
  compactRichMenuTestStartAt,
  incentivizedOAStartLevel,
  resubscriptionPromptCooldown,
  liveOpsMutePeriod,
} from './chatbot.ruleset';
import ab from '../ruleset/ab';
import { isDynamicTestInBucket } from '../getters/ab';
import { isGuestUser } from '../guest/guest.getters';
import { guessIsNativeAppInstalled } from '../native/native.getters';
import { NATIVE_MIGRATION_OPTIONAL_WINDOW } from '../native/native.ruleset';

/**
 * Get current chatbot group.
 * @param state
 * @returns
 */
export function getChatbotGroup(state: State): ChatbotGroup | undefined {
  return state.chatbot.group as ChatbotGroup | undefined;
}

/**
 * Check if can be sent on channel.
 * @param state - user state
 * @param messageType - chatbot notification type
 * @param now - current timestamp
 */
export function getChatbotMessageChannel(
  state: State,
  messageType:
    | { name: Exclude<ChatMessageType, 'status'> }
    | { name: 'status'; petState: PetState },
  now: number,
): 'oa' | 'push' | null {
  if (isGuestUser(state)) {
    return getChatbotMessageChannelForGuestMode(messageType);
  }

  const group = getChatbotGroup(state);

  if (messageType.name === 'liveOps') {
    return now - state.createdAt >= liveOpsMutePeriod ? 'push' : null;
  }

  if (!shouldReceiveReducedOA(state, group, now)) return 'oa';

  // Send sick, dying or dead status messages over OA.
  if (
    messageType.name === 'status' &&
    ['sick', 'dying', 'dead'].includes(messageType.petState)
  ) {
    return 'oa';
  }

  // Otherwise send push notification
  return 'push';
}

function getChatbotMessageChannelForGuestMode(
  messageType:
    | { name: Exclude<ChatMessageType, 'status'> }
    | { name: 'status'; petState: PetState },
): 'push' | null {
  // Send ONLY sick, dying or dead status messages over push.
  if (
    messageType.name === 'status' &&
    ['sick', 'dying', 'dead'].includes(messageType.petState)
  ) {
    return 'push';
  }

  // LiveOps and status messages are not sent in guest mode.
  if (messageType.name === 'liveOps' || messageType.name === 'status') {
    return null;
  }

  // All other messages are sent over push.
  return 'push';
}

export function getDefaultRichMenu(state: State): RichMenuName {
  return isDynamicTestInBucket(
    state,
    ab.tests.TEST_COMPACT_RICH_MENU,
    'enabled',
  )
    ? 'miniappCompact'
    : 'miniapp';
}

/**
 * Whether an OA subscription may enroll the user in the compact rich menu
 * test: only accounts created since the test started are eligible.
 */
export function isEligibleForCompactRichMenuTest(
  state: Pick<State, 'createdAt' | 'id'>,
): boolean {
  if (isGuestUser(state)) return false;
  return state.createdAt >= compactRichMenuTestStartAt;
}

/**
 * Returns the all-time view count for the legacy resubscribe surfaces (prompt /
 * force / direct_overlay). The SC onboarding surfaces keep their own counters —
 * see {@link getAddFriendPopupViewCount} and {@link getProfileConsentPopupViewCount}.
 */
export function getResubscribePopupViewCount(state: State): number {
  return state.chatbot.resubscribePopupViewCount;
}

/** Returns the all-time OA Add-Friend overlay view count (SC onboarding). */
export function getAddFriendPopupViewCount(state: State): number {
  return state.chatbot.addFriendPopupViewCount;
}

/** Returns the all-time profile-consent popover view count (SC onboarding). */
export function getProfileConsentPopupViewCount(state: State): number {
  return state.chatbot.profileConsentPopupViewCount;
}

/**
 * Check whether the user has already received a lifetime OA addition reward.
 *
 * @param state User state containing the legacy resubscription marker.
 * @returns Whether any OA incentive reward was previously claimed.
 */
export function hasClaimedIncentivizedOAReward(
  state: Pick<State, 'chatbot'>,
): boolean {
  // Reuse the existing permanent claim marker to avoid duplicate reward state.
  return state.chatbot.resubscriptionState === 'claimed';
}

/**
 * Check whether the incentivized OA surfaces may be offered to the user.
 *
 * @param state User state containing progression and reward claim data.
 * @returns Whether the user has unlocked and not claimed the treatment reward.
 */
export function canOfferIncentivizedOAAddition(
  state: Pick<State, 'chatbot' | 'match2'>,
): boolean {
  // The reward-bearing experience starts only after Puzzle 5 is completed.
  if (state.match2.lastLevel < incentivizedOAStartLevel) return false;

  // Any lifetime OA reward claim permanently suppresses this offer.
  if (hasClaimedIncentivizedOAReward(state)) return false;

  // All eligibility checks passed.
  return true;
}

export function canClaimResubscriptionReward(
  state: State,
  now: number,
): boolean {
  const { resubscriptionPromptTimestamp, resubscriptionState } = state.chatbot;

  // After the initial prompt, the user moves to the forced variant which has no reward.
  const shouldBeForced =
    resubscriptionPromptTimestamp! + resubscriptionPromptCooldown < now;

  return resubscriptionState === 'pending' && !shouldBeForced;
}

export function shouldReceiveReducedOA(
  state: State,
  group: ChatbotGroup | undefined,
  now: number,
): boolean {
  // Resolved: 0421_full_oa_for_non_native_users to 'enabled' for non-native users
  // Non-native users (no native app activity in last 30 days) receive full OA
  const nativeAppInstalled =
    guessIsNativeAppInstalled(
      state,
      'IOS',
      now,
      NATIVE_MIGRATION_OPTIONAL_WINDOW,
    ) ||
    guessIsNativeAppInstalled(
      state,
      'ANDROID',
      now,
      NATIVE_MIGRATION_OPTIONAL_WINDOW,
    );
  if (!nativeAppInstalled) {
    return false;
  }

  // Always send over OA for new adults.
  return group !== 'newAdult';
}
